// Side-panel rendering: tab switching, snapshot list, code-panel sync, URL state.

import type { EditorMenuItem } from '../editorMenu'
import { renderEditorMenu } from '../editorMenu'
import { SCRATCH_FILE_ID } from '../scratchBuffer'
import { getWorkspaceFiles } from '../fileStorage'
import { ICONS, escapeHtml, getActiveSnapshotDetails, replaceSnapshotView, updateCSS } from '../scripts'
import { getSavedSnapshots, getTerminalSnapshots } from '../snapshotStorage'
import { getState, saveState } from '../state'
import { getActiveTabKind } from './tabs'
import { state } from './playgroundState'

export const SIDE_SNAPSHOTS_VISIBLE = 5
type SideTabId = 'files' | 'snapshots'

function normalizeSideTab(tabId: string | null | undefined): SideTabId {
  if (tabId === 'snapshots') return tabId
  return 'files'
}

/**
 * Mirror the active editor tab to the URL bar via `path` query param.
 * The path is the workspace file's `path` field — works for all file types
 * (workspace files, scratch, handlers, saved snapshots, terminal snapshots).
 * Scratch is never written to the URL (it's the default).
 */
export function syncPlaygroundUrlState() {
  const url = new URL(window.location.href)
  const activeFileId = getState('current-file-id')
  if (activeFileId && activeFileId !== SCRATCH_FILE_ID) {
    const file = getWorkspaceFiles().find(f => f.id === activeFileId)
    if (file) {
      url.searchParams.set('path', file.path)
    } else {
      url.searchParams.delete('path')
    }
  } else {
    url.searchParams.delete('path')
  }

  // Strip legacy params that are no longer used.
  url.searchParams.delete('view')
  url.searchParams.delete('fileId')
  url.searchParams.delete('snapshotId')
  url.searchParams.delete('bindingName')
  url.searchParams.delete('contextEntryKind')

  history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
}

export function toggleSideSnapshotsShowAll() {
  state.sideSnapshotsShowAll = !state.sideSnapshotsShowAll
  populateSideSnapshotsList()
}

export function populateSideSnapshotsList() {
  const list = document.getElementById('side-snapshots-list')
  if (!list) return

  const terminalEntries = getTerminalSnapshots()
  const savedEntries = getSavedSnapshots()

  if (terminalEntries.length === 0 && savedEntries.length === 0) {
    list.innerHTML = '<div class="explorer-empty">No snapshots</div>'
    return
  }

  const items: string[] = []

  if (terminalEntries.length > 0) {
    items.push('<div class="explorer-group-label">Completed Files</div>')
    const ordinals = ['Last', '2nd Last', '3rd Last']
    const visibleCount = state.sideSnapshotsShowAll
      ? terminalEntries.length
      : Math.min(SIDE_SNAPSHOTS_VISIBLE, terminalEntries.length)
    for (let i = 0; i < visibleCount; i++) {
      const entry = terminalEntries[i]!
      const label = `${ordinals[i] ?? `${i + 1}th Last`} Run`
      const colorVar =
        entry.resultType === 'error'
          ? 'var(--color-error)'
          : entry.resultType === 'halted'
            ? 'var(--color-primary)'
            : 'var(--color-success)'
      const activeClass = state.activeSnapshotKey === `terminal:${i}` ? ' explorer-item--active' : ''
      const menuId = `side-terminal-menu-${i}`
      const menuItems: EditorMenuItem[] = [
        {
          action: `Playground.closeExplorerMenus();Playground.openTerminalSnapshot(${i})`,
          icon: ICONS.eye,
          label: 'Open',
        },
        {
          action: `Playground.closeExplorerMenus();Playground.saveTerminalSnapshotToSaved(${i})`,
          icon: ICONS.save,
          label: 'Save',
        },
        {
          action: `Playground.closeExplorerMenus();Playground.downloadTerminalSnapshotByIndex(${i})`,
          icon: ICONS.download,
          label: 'Download',
        },
        {
          action: `Playground.closeExplorerMenus();Playground.clearTerminalSnapshot(${i})`,
          danger: true,
          icon: ICONS.trash,
          label: 'Delete',
        },
      ]
      items.push(`
        <div class="explorer-item${activeClass}" onclick="Playground.openTerminalSnapshot(${i})" title="${escapeHtml(label)}">
          <span class="explorer-item__dot" style="background:${colorVar};"></span>
          <span class="explorer-item__name">${escapeHtml(label)}</span>
          <span class="explorer-item__actions" onclick="event.stopPropagation()">
            ${renderEditorMenu({ id: menuId, items: menuItems })}
            <button class="explorer-item__btn" onclick="Playground.toggleExplorerMenu('${menuId}', this)" title="More actions">${ICONS.menu}</button>
          </span>
        </div>`)
    }
    if (terminalEntries.length > SIDE_SNAPSHOTS_VISIBLE) {
      if (state.sideSnapshotsShowAll) {
        items.push('<div class="explorer-show-more" onclick="Playground.toggleSideSnapshotsShowAll()">Show less</div>')
      } else {
        items.push(
          `<div class="explorer-show-more" onclick="Playground.toggleSideSnapshotsShowAll()">Show all (${terminalEntries.length})</div>`,
        )
      }
    }
  }

  if (savedEntries.length > 0) {
    items.push('<div class="explorer-group-label">Saved Snapshots</div>')
    savedEntries.forEach((entry, i) => {
      const label = entry.name || `Snapshot ${i + 1}`
      const savedActiveClass = state.activeSnapshotKey === `saved:${i}` ? ' explorer-item--active' : ''
      const isSuspended = entry.snapshot.terminal !== true
      const menuId = `side-saved-menu-${i}`
      const menuItems: EditorMenuItem[] = [
        {
          action: `Playground.closeExplorerMenus();Playground.openSavedSnapshot(${i})`,
          icon: ICONS.eye,
          label: 'Open',
        },
        {
          action: `Playground.closeExplorerMenus();Playground.downloadSavedSnapshotByIndex(${i})`,
          icon: ICONS.download,
          label: 'Download',
        },
        {
          action: `Playground.closeExplorerMenus();Playground.deleteSavedSnapshot(${i})`,
          danger: true,
          icon: ICONS.trash,
          label: 'Delete',
        },
      ]
      const runButton = isSuspended
        ? `<button class="explorer-item__btn" onclick="event.stopPropagation();Playground.runSavedSnapshot(${i})" title="Run snapshot">${ICONS.play}</button>`
        : ''
      items.push(`
        <div class="explorer-item${savedActiveClass}" onclick="Playground.openSavedSnapshot(${i})" title="${escapeHtml(label)}">
          <span class="explorer-item__name">${escapeHtml(label)}</span>
          <span class="explorer-item__actions" onclick="event.stopPropagation()">
            ${runButton}
            ${renderEditorMenu({ id: menuId, items: menuItems })}
            <button class="explorer-item__btn" onclick="Playground.toggleExplorerMenu('${menuId}', this)" title="More actions">${ICONS.menu}</button>
          </span>
        </div>`)
    })
  }

  list.innerHTML = items.join('')
}

export function showSideTab(tabId: string, options: { persist?: boolean; syncUrl?: boolean } = {}) {
  const normalizedTabId = normalizeSideTab(tabId)
  // Hide all side tabs, show the selected one
  document.querySelectorAll('.side-panel__tab').forEach(el => {
    ;(el as HTMLElement).style.display = 'none'
  })
  const tab = document.getElementById(`side-tab-${normalizedTabId}`)
  if (tab) tab.style.display = ''

  // Update icon active state
  document.querySelectorAll('.side-panel__icon').forEach(el => el.classList.remove('side-panel__icon--active'))
  const icon = document.getElementById(`side-icon-${normalizedTabId}`)
  if (icon) icon.classList.add('side-panel__icon--active')

  // Clear the "new snapshot" indicator when entering the snapshot view.
  if (normalizedTabId === 'snapshots')
    document.getElementById('side-icon-snapshots')?.classList.remove('side-panel__icon--has-new')

  document.querySelectorAll('[id^="side-header-"]').forEach(el => {
    if (el.id === 'side-panel-header') return
    if (el.id.startsWith('side-header-actions-') || el.id.startsWith('side-header-')) {
      ;(el as HTMLElement).style.display = 'none'
    }
  })

  const header = document.getElementById(`side-header-${normalizedTabId}`)
  if (header) header.style.display = ''

  const actions = document.getElementById(`side-header-actions-${normalizedTabId}`)
  if (actions) actions.style.display = ''

  if (options.persist !== false) saveState({ 'active-side-tab': normalizedTabId }, false)

  if (options.syncUrl !== false) syncPlaygroundUrlState()

  // Sync the code panel view and header
  syncCodePanelView()
  updateCSS()
}

/** Sync the code panel: show editor, snapshot, or empty view + update header accordingly. */
function setEditorEmptyState(
  emptyView: HTMLElement,
  title: string,
  description: string,
  buttonLabel: string,
  action: string,
) {
  emptyView.innerHTML = `
    <div class="dvala-empty-view__content">
      <div class="dvala-empty-view__title">${escapeHtml(title)}</div>
      <div class="dvala-empty-view__description">${escapeHtml(description)}</div>
      <button type="button" class="button button--primary dvala-empty-view__button" onclick="${action}">${escapeHtml(buttonLabel)}</button>
    </div>
  `
}

/**
 * Render the editor area to match the active editor tab. Phase 1.5 step
 * 23j stage 2 decoupled this from the side-tab — file tabs show the
 * editor view, snapshot tabs show the snapshot view, and there's no
 * "select a snapshot" empty state because the side-tab list is now a
 * pure left-panel concern (clicking an item opens the corresponding tab,
 * which drives the editor area through this function).
 */
export function syncCodePanelView() {
  const editorView = document.getElementById('dvala-editor-view')
  const snapshotView = document.getElementById('dvala-snapshot-view')
  const emptyView = document.getElementById('dvala-empty-view')
  if (!editorView || !snapshotView || !emptyView) return

  // Hide all views
  editorView.style.display = 'none'
  snapshotView.style.display = 'none'
  emptyView.style.display = 'none'

  const activeKind = getActiveTabKind()

  if (activeKind === 'snapshot') {
    // Snapshot tab is active. The snapshot view's `#snapshot-content` is
    // populated by `replaceSnapshotView` (called from the side-panel
    // click flow and the after-tab-swap hook); we just need to make the
    // container visible.
    snapshotView.style.display = 'flex'
    // Fall through if there's no snapshot data to render — show the
    // empty/import state. Happens transiently between "tab created" and
    // "snapshot data loaded into the panel".
    if (!state.activeSnapshotKey || state.snapshotViewStack.length === 0) {
      const activeSnapshot = getActiveSnapshotDetails()
      if (activeSnapshot) {
        replaceSnapshotView(activeSnapshot.snapshot, activeSnapshot.label)
        updateCSS()
        return
      }
    }
    return
  }

  if (activeKind === 'file') {
    editorView.style.display = 'flex'
    return
  }

  // No active tab — empty state.
  emptyView.style.display = 'flex'
  setEditorEmptyState(
    emptyView,
    'No tab open',
    'Open a file from the Files panel or import a snapshot from the Snapshots panel.',
    'Open scratch',
    'Playground.openScratch()',
  )
}

export function getCurrentSideTab(): string {
  const active = document.querySelector('.side-panel__icon--active')
  if (!active) return getState('active-side-tab')
  const id = active.id
  if (id === 'side-icon-snapshots') return 'snapshots'
  return 'files'
}
